// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What this player's eyes actually do, remembered across sessions (M5-33).
//
// `comfort.ts` can already tell a believable floor from an impossible one. What it cannot do is
// say what the *right* answer would have been, and that is the gap this file fills: when the
// runtime's floor is refused, something has to stand in for it, and a constant is the wrong shape
// of thing to stand in with.
//
// **The floor's error is not stable and the player's height is.** Measured on one Quest 3 in one
// room, the disagreement between the guardian floor and the SLAM floor read 3.07 m, 1.69 m and
// 1.74 m on three separate readings. A build that measures that error once and stores it is
// wrong by metres the next time — and wrong in the *other* direction, which puts the player under
// the room instead of above it. The same person standing in the same room is 1.65 m every time.
// So nothing here remembers the floor. It remembers the person, and the correction is recomputed
// from whatever the runtime is claiming right now.
//
// **The median, not the mean.** A player who sits down for one session and stands for eight
// should not be dragged toward the odd one out, and a reading taken while the headset is being
// lifted off should count for nothing. A median of the last nine does both without needing to
// know which reading was which.
//
// **Implausible readings are dropped on read, not refused on write.** A value stored by a build
// with a different believability band must not survive a change to that band, and the only way to
// guarantee that is to re-judge every sample every time it is loaded.

import { believableFloor, NOMINAL_SEATED_EYE } from './comfort'
import type { StorageLike } from '../storage/local'

/** Where the samples live. Namespaced like every other key this product writes. */
export const EYE_HEIGHT_KEY = 'hcc.pinball.xr.eyeHeight'

/**
 * How many good readings to keep.
 *
 * Nine. Odd, so the median is a reading this player actually produced rather than the average of
 * two of them, and small enough that a player who changes chairs is described by their recent
 * sessions rather than by a year of them.
 */
export const SAMPLE_LIMIT = 9

/**
 * The eye height used before this device has ever seen a good floor.
 *
 * Shared with `comfort.ts` rather than declared again, because it is the same decision: a floor
 * broken badly enough to be refused is almost always a headset whose space was set up at a desk,
 * and a player at a desk is sitting at it. Being 15 cm out beats being 1.6 m out.
 */
export const NOMINAL_EYE = NOMINAL_SEATED_EYE

/** The good readings this device has seen, oldest first. */
export function loadEyeHeights(storage: StorageLike | undefined): readonly number[] {
  if (storage === undefined) return []
  let raw: string | null
  try {
    raw = storage.getItem(EYE_HEIGHT_KEY)
  } catch {
    return []
  }
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((entry) => (typeof entry === 'number' ? entry : Number.NaN))
      .filter(believableFloor)
      .slice(-SAMPLE_LIMIT)
  } catch {
    // Hand-edited or truncated storage. A session must not fail over a memory it can rebuild.
    return []
  }
}

/**
 * Record one reading, if it is one a body could have produced.
 *
 * Refused rather than clamped: this memory is only ever taught by floors that were working, and a
 * clamped implausible value would teach it that the broken floor was nearly right.
 */
export function rememberEyeHeight(storage: StorageLike | undefined, meters: number): void {
  if (storage === undefined || !believableFloor(meters)) return
  const next = [...loadEyeHeights(storage), meters].slice(-SAMPLE_LIMIT)
  try {
    storage.setItem(EYE_HEIGHT_KEY, JSON.stringify(next))
  } catch {
    // Quota, or a private window. The reading still serves this session; only the memory is lost.
  }
}

/** This player's eye height as best it is known, or the nominal fallback before it is. */
export function knownEyeHeight(storage: StorageLike | undefined): number {
  const samples = loadEyeHeights(storage)
  if (samples.length === 0) return NOMINAL_EYE
  const sorted = [...samples].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  const upper = sorted[middle] ?? NOMINAL_EYE
  if (sorted.length % 2 === 1) return upper
  return ((sorted[middle - 1] ?? upper) + upper) / 2
}

/**
 * How far to move the world so a reported eye height reads as this player's own, or `undefined`
 * to leave the runtime alone.
 *
 * **Sign, written out, because backwards is how a player ends up under the floor.** A space
 * offset by `o` reports a point that was at `y` as `y − o`. To make a reported 0.01 m read as a
 * target 1.62 m needs `o = 0.01 − 1.62 = −1.61`: the origin drops by 1.61 m, which puts the
 * virtual floor back down on the player's real one. Hence `reported − target`.
 *
 * `reported` must come from a **freshly re-read** runtime space and never from one this function
 * has already offset. Recomputing against the runtime's own floor every time is what stops
 * corrections compounding, and compounding is the failure mode that makes this worse than doing
 * nothing.
 */
export function floorCorrection(reported: number, known: number): number | undefined {
  if (!Number.isFinite(reported)) return undefined
  // A posture, not a fault. Tall players and low couches are left exactly as the runtime says.
  if (believableFloor(reported)) return undefined
  const target = believableFloor(known) ? known : NOMINAL_EYE
  return reported - target
}

/**
 * The whole exchange in one call: judge the reading, learn from it or correct for it.
 *
 * Returns the correction to apply, or `undefined` when the floor was fine — in which case the
 * reading has been *remembered*, because a working floor is the only thing that can teach this
 * memory anything.
 */
export function observeEyeHeight(
  storage: StorageLike | undefined,
  reported: number,
): number | undefined {
  const correction = floorCorrection(reported, knownEyeHeight(storage))
  if (correction === undefined) {
    rememberEyeHeight(storage, reported)
    return undefined
  }
  return correction
}
